// @owners { team: patients-team }
import { BORDERS, COLORS } from '@alto/design-library-tokens';
import React, { type ReactNode } from 'react';
import styled from 'styled-components';
import { StyledTouchable, type StyledTouchableProps } from '~web/features/essentials/primitives/Styled';

type Props = {
  readonly children?: ReactNode | ReactNode[];
  readonly display?: 'flex' | 'inline' | 'block' | 'inline-block' | 'none';
  readonly position?: 'sticky' | 'absolute' | 'relative' | 'static' | 'fixed';
  readonly flexDirection?: 'row' | 'column';
  readonly borderRadius?: keyof typeof BORDERS.RADIUS | 'initial';
  readonly justifyContent?: 'center' | 'flex-end' | 'flex-start' | 'space-between' | 'space-around' | 'normal';
  readonly alignItems?: 'center' | 'flex-end' | 'flex-start' | 'normal';
  readonly alignSelf?: 'center' | 'flex-end' | 'flex-start' | 'normal';
  readonly alignContent?: 'center' | 'flex-end' | 'flex-start' | 'normal';
  readonly flexGrow?: number | 'initial';
  readonly flexShrink?: number | 'initial';
  readonly flexWrap?: 'wrap' | 'nowrap' | 'initial';
  readonly flexBasis?: string;
  readonly padding?: string;
  readonly margin?: string;
  readonly maxWidth?: string;
  readonly width?: string;
  readonly height?: string;
  readonly style?: React.CSSProperties;
  readonly cursor?: 'default' | 'pointer';
  readonly overflow?: 'scroll' | 'visible' | 'hidden' | 'initial';
  readonly opacity?: number;
  readonly onClick?: (event: React.SyntheticEvent<HTMLElement>) => void;
  readonly backgroundColor?: keyof typeof COLORS.BACKGROUND_COLORS | 'transparent';
};

const StyledView = styled.div<Props & StyledTouchableProps>`
  padding: ${({ padding = '0px' }) => padding};
  margin: ${({ margin = '0px' }) => margin};
  max-width: ${({ maxWidth = '' }) => maxWidth};
  width: ${({ width = '' }) => width};
  height: ${({ height = '' }) => height};
  position: ${({ position = 'static' }) => position};
  border-radius: ${({ borderRadius = 'initial' }) =>
    borderRadius === 'initial' ? 'initial' : BORDERS.RADIUS[borderRadius].px}};
  background-color: ${({ backgroundColor = 'transparent' }) =>
    backgroundColor === 'transparent' ? 'transparent' : COLORS.BACKGROUND_COLORS[backgroundColor]};
  display: ${({ display = 'flex' }) => display}; 
  flex-direction: ${({ flexDirection = 'row' }) => flexDirection};
  justify-content: ${({ justifyContent = 'normal' }) => justifyContent};
  align-items: ${({ alignItems = 'normal' }) => alignItems};
  align-self: ${({ alignSelf = 'normal' }) => alignSelf};
  align-content: ${({ alignContent = 'normal' }) => alignContent};
  flex-wrap: ${({ flexWrap = 'initial' }) => flexWrap};
  flex-grow: ${({ flexGrow = 'initial' }) => flexGrow};
  flex-shrink: ${({ flexShrink = 'initial' }) => flexShrink};
  flex-basis: ${({ flexBasis = 'auto' }) => flexBasis};
  cursor: ${({ cursor = 'default' }) => cursor};
  overflow: ${({ overflow = 'initial' }) => overflow};
  ${({ onClick }) => onClick && StyledTouchable};
  opacity: ${({ opacity = 1 }) => opacity};
`;

export const View = ({ children, ...rest }: Props) => {
  return <StyledView {...rest}>{children}</StyledView>;
};
